<template>
  <div class="pricing-plans">
    <div v-if="loading" class="pricing-plans__state">
      <ProgressSpinner style="width: 42px; height: 42px" strokeWidth="4" />
    </div>

    <div v-else-if="error" class="pricing-plans__state">
      <p class="ds-error">We couldn't load the plans right now.</p>
      <a v-if="fallbackUrl" :href="fallbackUrl" class="ds-link">
        View pricing details
      </a>
    </div>

    <template v-else>
      <div class="pricing-plans__grid">
        <div
          v-for="price in cards"
          :key="price.id"
          class="plan-card"
          :class="{ 'plan-card--popular': price.popular }"
        >
          <!-- Rendered on every card, empty on most, so the card bodies stay
               aligned across the row whether or not a badge is present. -->
          <div class="plan-card__badge-row">
            <span v-if="price.popular" class="plan-card__badge">
              Our most popular package
            </span>
          </div>

          <h3 class="plan-card__name">{{ price.name }}</h3>
          <p class="plan-card__audience">{{ audience }}</p>

          <div class="plan-card__amount">{{ price.headline }}</div>
          <p class="plan-card__terms">{{ price.terms }}</p>

          <p v-if="price.feature" class="plan-card__feature">
            {{ price.feature }}
          </p>
          <p v-if="price.support" class="plan-card__support">
            {{ price.support }}
          </p>

          <!-- Absorbs the leftover height so the three CTAs line up even when
               the copy above them runs to different numbers of lines, while
               still guaranteeing a minimum gap on the tallest card. -->
          <div class="plan-card__spacer"></div>

          <button
            type="button"
            class="ds-button ds-button--primary ds-button--block plan-card__cta"
            :disabled="!!checkingOut"
            @click="choose(price.id)"
          >
            {{ checkingOut === price.id ? "Redirecting…" : "Choose plan" }}
          </button>
        </div>
      </div>

      <p v-if="hasMultiSeat" class="pricing-plans__note">
        Two- and three-user plans are managed by whoever subscribes: you become
        the account owner and invite colleagues to the remaining seats from
        inside CurriculumFinder.
      </p>
    </template>
  </div>
</template>

<script>
import {
  getPaidPlan,
  sortPricesByTier,
  startPlanCheckout,
  tierOf,
  TIER_SEATS,
} from "../auth";

// Presentation copy per tier, mirroring the marketing pricing page so a member
// who arrives from there sees the same offer described the same way. Memberstack
// carries only name/amount/interval, so this cannot be read from the API — if
// the pricing page copy changes, change it here too.
// Source: https://degreedata.webflow.io/pricing
const TIER_COPY = {
  single: {
    feature: "Unlimited access for a single user",
    support: "Get your job done faster and accurately with one-stop research.",
  },
  two: {
    feature: "Unlimited access for 2 users",
    support: "Save over 10% over the single user seat.",
    popular: true,
  },
  three: {
    feature: "Unlimited access for 3 users",
    support: "A great value for power users!",
  },
};

const AUDIENCE = "For colleges, universities, non-profits";

export default {
  name: "PricingPlans",
  data() {
    return {
      loading: true,
      error: false,
      prices: [],
      checkingOut: null,
      audience: AUDIENCE,
      // Shown only if the picker fails to load, so the user is never dead-ended.
      fallbackUrl: process.env.VUE_APP_PRICING_URL ?? "",
    };
  },
  computed: {
    // Each price decorated with everything the card renders. Anything derived
    // from the tier is optional: a price whose name matches no tier still gets
    // a card, just with the raw amount and no marketing copy — the same visible
    // degradation sortPricesByTier() uses, rather than vanishing from the list.
    cards() {
      return this.prices.map((price) => {
        const tier = tierOf(price);
        const copy = (tier && TIER_COPY[tier]) || {};
        const seats = (tier && TIER_SEATS[tier]) || null;
        const yearly = this.isYearly(price);

        return {
          id: price.id,
          name: price.name,
          tier,
          popular: !!copy.popular,
          feature: copy.feature || "",
          support: copy.support || "",
          headline:
            seats && yearly
              ? this.money(Number(price.amount) / 12 / seats, price)
              : this.money(Number(price.amount), price),
          terms: this.termsLine(price, seats, yearly),
        };
      });
    },
    hasMultiSeat() {
      return this.cards.some((c) => c.tier === "two" || c.tier === "three");
    },
  },
  async mounted() {
    try {
      const plan = await getPaidPlan();
      // Always Single, Two, Three — Memberstack's own order follows how the
      // prices were created, which is not something we want to depend on.
      this.prices = sortPricesByTier(
        (plan?.prices ?? []).filter(
          (p) => (p.status || "").toUpperCase() === "ACTIVE"
        )
      );
      if (!this.prices.length) this.error = true;
    } catch (err) {
      console.error("[pricing] failed to load plan:", err);
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    isYearly(price) {
      return (price.interval?.type || "").toUpperCase() === "YEARLY";
    },
    // Memberstack returns the amount in whole currency units
    // (e.g. 2160 = $2,160/yr), not cents — so no division by 100.
    money(value, price) {
      const amount = Number(value);
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: (price.currency || "usd").toUpperCase(),
          // Whole dollars stay clean ($180, not $180.00); an awkward per-seat
          // division still shows its cents rather than silently rounding.
          minimumFractionDigits: 0,
          maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
        }).format(amount);
      } catch (err) {
        return `$${amount}`;
      }
    },
    // "per month · billed annually ($2,160 per year)" for a single seat, and
    // "per user, per month · billed annually ($2,880 per year for 2 seats)"
    // above that — the same phrasing as the pricing page. Falls back to a plain
    // interval label when the price isn't a yearly tier we recognise.
    termsLine(price, seats, yearly) {
      if (!seats || !yearly) return `per ${this.intervalLabel(price)}`;
      const total = this.money(Number(price.amount), price);
      const per = seats > 1 ? "per user, per month" : "per month";
      const forSeats = seats > 1 ? ` for ${seats} seats` : "";
      return `${per} · billed annually (${total} per year${forSeats})`;
    },
    intervalLabel(price) {
      const type = (price.interval?.type || "").toLowerCase();
      const map = {
        daily: "day",
        weekly: "week",
        monthly: "month",
        yearly: "year",
      };
      return map[type] || type || "term";
    },
    async choose(priceId) {
      if (this.checkingOut) return;
      this.checkingOut = priceId;
      try {
        // Redirects to Stripe. If it returns without navigating away, treat as
        // a failure so the button re-enables and the user can retry.
        await startPlanCheckout({ priceId });
      } catch (err) {
        console.error("[pricing] checkout failed:", err);
        this.checkingOut = null;
        window.alert("We couldn't start checkout. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
.pricing-plans__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
}

.pricing-plans__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (min-width: 720px) {
  .pricing-plans__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.plan-card {
  display: flex;
  flex-direction: column;
  padding: 18px 20px 22px;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-card);
  /* The trial-ended screen centres its contents; these cards read as a list of
     facts and need to stay left-aligned regardless of where they're mounted. */
  text-align: left;
}

.plan-card--popular {
  border-color: color-mix(in oklab, var(--brand-orange) 45%, transparent);
}

/* Present but empty on the other cards so all three bodies start on the same
   line, matching the pricing page where the badge sits above the middle card. */
.plan-card__badge-row {
  min-height: 22px;
  margin-bottom: 2px;
}

.plan-card__badge {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-orange);
}

.plan-card__name {
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ink);
  margin: 0;
}

.plan-card__audience {
  margin: 6px 0 0;
  font-size: 0.8rem;
  color: var(--mute);
}

.plan-card__amount {
  margin-top: 14px;
  font-family: var(--font-sans);
  font-size: 2.15rem;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.plan-card__terms {
  margin: 6px 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--mute);
}

.plan-card__feature {
  margin: 16px 0 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ink);
}

.plan-card__support {
  margin: 4px 0 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--ink-soft);
}

.plan-card__spacer {
  flex: 1 1 auto;
  min-height: 18px;
}

.pricing-plans__note {
  margin: 18px auto 0;
  max-width: 60ch;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--mute);
  text-align: center;
}
</style>
